import React from "react";

import { Grid, Skeleton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Subdirectory from "@mui/icons-material/SubdirectoryArrowRight";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
  border: {
    borderBottom: `1px solid ${theme.palette.backgrounds.primary}`,
    borderTop: `1px solid ${theme.palette.backgrounds.primary}`,
    padding: theme.spacing(0.5, 0),
  },
  tooltip: {
    color: theme.palette.secondary.light,
    marginRight: "auto",
    marginLeft: theme.spacing(1),
    "&&:hover": {
      color: theme.palette.primary.main,
    },
  },
  default: {
    color: theme.palette.text.primary,
  },
  positive: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: theme.typography.h2.fontSize,
  },
  negative: {
    color: theme.palette.error.main,
  },
  indented: {
    color: theme.palette.text.primary,
  },
}));

export const DataTable: React.FC<DataTableProps> = ({
  data,
  isLoading,
  detailTable = false,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <Grid container direction="column" gap={theme.spacing(1)}>
      {data?.map(({ key, value, StartIcon, variant, tooltipText }) => (
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          key={key}
          className={variant === "positive" ? classes.border : ""}
        >
          {variant === "indented" && (
            <Subdirectory color="secondary" sx={{ mr: 1 }} />
          )}
          {StartIcon}
          <Typography
            sx={StartIcon ? { ml: 2 } : {}}
            variant={detailTable ? "caption" : "body1"}
          >
            {key}
          </Typography>

          {tooltipText && (
            <Tooltip
              title={tooltipText}
              className={classes.tooltip}
              enterTouchDelay={0}
              leaveDelay={2000}
            >
              <HelpOutlineIcon />
            </Tooltip>
          )}

          <Typography
            variant={detailTable ? "caption" : "body1"}
            sx={{ flex: 1 }}
            align="right"
            fontWeight={detailTable ? "bold" : "normal"}
            className={classes[`${variant || "default"}`]}
          >
            {isLoading ? <Skeleton sx={{ ml: "auto" }} width={100} /> : value}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};

export interface DataTableProps {
  data: {
    /* The key or label for the item. */
    key: string;
    /* The value displayed on the right. */
    value: string;
    /* The tooltip text for the item. Optional. */
    tooltipText?: string;
    /* Should the line be displayed as a regular line (default) or have a highlight (positive or negative)  */
    variant?: "default" | "positive" | "negative" | "indented";
    StartIcon?: React.ReactElement;
  }[];
  isLoading?: boolean;
  detailTable?: boolean;
}
